import React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import PhoneIcon from "@mui/icons-material/Phone";
import { SoftphoneSipConfig } from "../hooks/useSoftphoneConfig";
import { useTranslation } from "../i18n/LocaleContext";

export const MISSING_NUMBER_PLACEHOLDER = "---";

interface DialIconButtonProps {
  number: string;
  onDial: (number: string) => void;
}

export function DialIconButton({ number, onDial }: DialIconButtonProps) {
  const { t } = useTranslation();

  if (number === MISSING_NUMBER_PLACEHOLDER || !number) {
    return <>{number}</>;
  }

  return (
    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <button
        type="button"
        onClick={() => onDial(number)}
        aria-label={t("dialControls.dialAria", { number })}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          border: 0,
          background: "transparent",
          color: "inherit",
          cursor: "pointer",
        }}
      >
        <PhoneIcon style={{ color: "#0c5a35", fontSize: 20 }} />
      </button>
      {number}
    </span>
  );
}

interface DialConfirmationDialogProps {
  sipConfig: SoftphoneSipConfig | null;
  selectedNumber: string;
  dialLoading: boolean;
  dialError: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function DialConfirmationDialog({
  sipConfig,
  selectedNumber,
  dialLoading,
  dialError,
  onCancel,
  onConfirm,
}: DialConfirmationDialogProps) {
  const { t } = useTranslation();

  // O mesmo selectedNumber controla a visibilidade e o texto; ao cancelar ele e zerado
  // enquanto o Dialog ainda executa a animacao de fechamento. Congelar o ultimo valor
  // evita que o numero (e o erro) sumam do texto antes do dialogo desaparecer.
  const lastNumberRef = React.useRef(selectedNumber);
  const lastErrorRef = React.useRef(dialError);
  if (selectedNumber) {
    lastNumberRef.current = selectedNumber;
    lastErrorRef.current = dialError;
  }
  const displayNumber = selectedNumber || lastNumberRef.current;
  const displayError = selectedNumber ? dialError : lastErrorRef.current;

  return (
    <Dialog open={!!selectedNumber} onClose={onCancel}>
      <DialogContent>
        <DialogContentText>
          {t("dialControls.confirmCopy", {
            number: displayNumber,
            extension: sipConfig?.extension || MISSING_NUMBER_PLACEHOLDER,
          })}
        </DialogContentText>
        {!!displayError && (
          <DialogContentText sx={{ marginTop: 2, color: "error.main" }}>
            {displayError}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={dialLoading}>{t("dialControls.cancelButton")}</Button>
        <Button onClick={onConfirm} variant="contained" disabled={dialLoading || !selectedNumber}>
          {dialLoading ? t("dialControls.dialingButton") : t("dialControls.okButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
